"use client";

import { c as _c } from "react/compiler-runtime";
import { getStripe } from "@/utils/stripe/client";
import { checkoutWithStripe } from "@/utils/stripe/server";
import { getErrorRedirect } from "@/utils/helpers";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Plus } from "lucide-react";
import { useId, useSyncExternalStore } from "react";
import { useLocale, useTranslations } from "next-intl";
import { getCheckoutOwner, runExclusiveCheckout, subscribeCheckout, type CheckoutRunResult } from "@/lib/checkout-lock";
import { runCheckoutFlow } from "@/lib/checkout-flow";
import { createCheckoutTiming } from "@/lib/checkout-timing";
import { getTrackingPricing } from "@/utils/tracking/pricing";
import { createInitiateCheckoutEventId, getCheckoutTrackingContext } from "@/utils/tracking/checkout";
import { sendTikTokInitiateCheckoutEvent } from "@/actions/tiktokConversion";
import { getTrackingConsent } from "@/hooks/useTrackingConsent";
import { matchCrawlerUserAgent } from "@/lib/crawler-user-agent";
import { readAdRefFromLocation } from "@/lib/ad-ref";
import { usePostHog } from "posthog-js/react";
import type { PublicOffer } from "@/lib/offers";
import { cn } from "@/lib/utils";
interface BuyEventButtonProps {
  className?: string;
  variant: "dark" | "light" | "lightest" | "christmas" | "white" | "konfirmation" | "barnedab" | "fodselsdag" | "barMitsva";
  packageType: "basic" | "premium";
  userEmail?: string;
  offerPlacement?: string;
  offer?: PublicOffer;
  /** Replaces "Buy event", e.g. "Choose Premium". */
  label?: string;
  /** "pill" is the landing page's rounded button with an arrow at the end. */
  appearance?: "default" | "pill";
  /** Colours for the pill's arrow circle. */
  arrowClassName?: string;
}
const getNoCheckoutOwner = (): null => null;
export default function BuyEventButton(t0) {
  const $ = _c(52);
  const {
    className,
    variant,
    packageType,
    userEmail,
    offerPlacement,
    offer,
    label,
    appearance: t1,
    arrowClassName
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const posthog = usePostHog();
  const checkoutId = useId();
  const checkoutOwner = useSyncExternalStore(subscribeCheckout, getCheckoutOwner, getNoCheckoutOwner);
  const isCheckoutBusy = checkoutOwner !== null;
  const isOpeningCheckout = checkoutOwner === checkoutId;
  let handleStripeCheckout;
  if ($[0] !== checkoutId || $[1] !== locale || $[2] !== offer || $[3] !== offerPlacement || $[4] !== packageType || $[5] !== pathname || $[6] !== posthog || $[7] !== router || $[8] !== userEmail) {
    handleStripeCheckout = () => runExclusiveCheckout(checkoutId, isCurrent => startCheckout(isCurrent)).catch(_temp);
    const startCheckout = isCurrent_0 => {
      const timing = createCheckoutTiming(isCurrent_0);
      const {
        checkoutFlow,
        checkoutPlace,
        checkoutLocale
      } = getCheckoutTrackingContext(pathname, locale);
      posthog.capture("clicked_buy_event", {
        package_type: packageType,
        packageType,
        platform: "web",
        checkout_flow: checkoutFlow,
        checkout_place: checkoutPlace,
        checkout_locale: checkoutLocale,
        is_upgrade: false,
        offer_placement: offerPlacement,
        offer_campaign_code: offer?.code
      });
      const checkoutLocaleValue = checkoutLocale;
      const fallbackPricing = getTrackingPricing(locale, packageType);
      const pricing = offer?.priceMinor === null || offer?.priceMinor === undefined ? fallbackPricing : {
        value: offer.priceMinor / 100,
        currency: offer.currency
      };
      const eventId = createInitiateCheckoutEventId();
      const hasAdvertisingConsent = getTrackingConsent().advertisement;
      const isNamedCrawler = matchCrawlerUserAgent(navigator.userAgent) !== null;
      if (hasAdvertisingConsent && !isNamedCrawler && window.fbq) {
        window.fbq("track", "InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency
        }, {
          eventID: eventId
        });
      }
      if (hasAdvertisingConsent && !isNamedCrawler && window.ttq) {
        window.ttq.track("InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency,
          contents: [{
            content_id: "guestgallery_subscription",
            content_type: "product",
            quantity: 1,
            price: pricing.value
          }],
          content_type: "product"
        });
      }
      if (hasAdvertisingConsent && !isNamedCrawler && window.gtag) {
        window.gtag("event", "begin_checkout", {
          value: pricing.value,
          currency: pricing.currency,
          items: [{
            item_id: "guestgallery_subscription",
            item_name: `Guest.Gallery ${packageType}`,
            quantity: 1,
            price: pricing.value
          }]
        });
      }
      if (userEmail && !isNamedCrawler) {
        sendTikTokInitiateCheckoutEvent(userEmail, checkoutLocaleValue, packageType, eventId);
      }
      const adRef = readAdRefFromLocation(window.location);
      return runCheckoutFlow({
        startSession: () => checkoutWithStripe(null, "/thank-you", checkoutLocaleValue, checkoutPlace, packageType, offerPlacement, eventId, adRef),
        getStripe,
        navigate: url => router.push(url),
        captureFailure: failure => posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          ...failure,
          platform: "web",
          offer_placement: offerPlacement,
          offer_campaign_code: offer?.code
        }),
        missingSessionRedirect: getErrorRedirect("/home", "An unknown error occurred.", "Please try again later or contact a system administrator."),
        timing
      }, isCurrent_0);
    };
    $[0] = checkoutId;
    $[1] = locale;
    $[2] = offer;
    $[3] = offerPlacement;
    $[4] = packageType;
    $[5] = pathname;
    $[6] = posthog;
    $[7] = router;
    $[8] = userEmail;
    $[9] = handleStripeCheckout;
  } else {
    handleStripeCheckout = $[9];
  }
  const t = useTranslations("Home.BuyEventCard");
  if (appearance === "pill") {
    const t2 = isOpeningCheckout && "disabled:opacity-100";
    let t3;
    if ($[10] !== className || $[11] !== t2) {
      t3 = cn("group h-12 w-full justify-between gap-3 rounded-full py-1.5 pl-6 pr-1.5 text-base font-semibold transition-[transform,background-color] duration-150 ease-[var(--ease-out-strong)] active:scale-[0.97]", t2, className);
      $[10] = className;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== handleStripeCheckout) {
      t4 = () => {
        handleStripeCheckout();
      };
      $[13] = handleStripeCheckout;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== isOpeningCheckout || $[16] !== label || $[17] !== t) {
      t5 = isOpeningCheckout ? t("openingCheckout") : label ?? t("buyEvent");
      $[15] = isOpeningCheckout;
      $[16] = label;
      $[17] = t;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== arrowClassName) {
      t6 = cn("grid h-9 w-9 shrink-0 place-items-center rounded-full transition-transform duration-300 ease-[var(--ease-overshoot)] can-hover:group-hover:translate-x-0.5 can-hover:group-hover:-rotate-12", arrowClassName);
      $[19] = arrowClassName;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== isOpeningCheckout) {
      t7 = isOpeningCheckout ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />;
      $[21] = isOpeningCheckout;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== t6 || $[24] !== t7) {
      t8 = <span aria-hidden="true" className={t6}>{t7}</span>;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    let t9;
    if ($[26] !== isCheckoutBusy || $[27] !== isOpeningCheckout || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t8 || $[32] !== variant) {
      t9 = <Button className={t3} disabled={isCheckoutBusy} aria-busy={isOpeningCheckout} variant={variant} onClick={t4}>{t5}{t8}</Button>;
      $[26] = isCheckoutBusy;
      $[27] = isOpeningCheckout;
      $[28] = t3;
      $[29] = t4;
      $[30] = t5;
      $[31] = t8;
      $[32] = variant;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    return t9;
  }
  let t2;
  if ($[34] !== handleStripeCheckout) {
    t2 = () => {
      handleStripeCheckout();
    };
    $[34] = handleStripeCheckout;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  let t3;
  if ($[36] !== isOpeningCheckout) {
    t3 = isOpeningCheckout ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />;
    $[36] = isOpeningCheckout;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  let t4;
  if ($[38] !== isOpeningCheckout || $[39] !== label || $[40] !== t) {
    t4 = isOpeningCheckout ? t("openingCheckout") : label ?? t("buyEvent");
    $[38] = isOpeningCheckout;
    $[39] = label;
    $[40] = t;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  let t5;
  if ($[42] !== t3 || $[43] !== t4) {
    t5 = <span className="flex items-center">{t3}{t4}</span>;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] !== className || $[46] !== isCheckoutBusy || $[47] !== isOpeningCheckout || $[48] !== t2 || $[49] !== t5 || $[50] !== variant) {
    t6 = <Button className={className} disabled={isCheckoutBusy} aria-busy={isOpeningCheckout} variant={variant} onClick={t2}>{t5}</Button>;
    $[45] = className;
    $[46] = isCheckoutBusy;
    $[47] = isOpeningCheckout;
    $[48] = t2;
    $[49] = t5;
    $[50] = variant;
    $[51] = t6;
  } else {
    t6 = $[51];
  }
  return t6;
}
function _temp() {}